import { useEffect, useState } from "react"
import { getAssetUrl } from "../utils"

// Self-hosted (#787): vite.build.ts copies leaflet/dist/leaflet.css (+ images/)
// into assets/dist, so it ships through our own hashed pipeline. This client
// const uses the /w/ 302 form for pure-CSR embeds; SSR pages carry the hashed
// URL in their css arrays (explorer/property-map ssr.ts via resolveAssetPath)
// and the mount-time dedupe drops this extra in favor of it.
export const LEAFLET_CSS = getAssetUrl('w/leaflet.css')
// JS stays on unpkg: it loads lazily post-mount (map init), outside the
// shadow-CSS reveal window this fix targets.
export const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'

/**
 * Dynamically loads Leaflet JS and returns true once ready.
 * CSS is NOT loaded here — it must be injected into the shadow root
 * via the mount() cssUrls config so it applies inside shadow DOM.
 * Safe to call from multiple widgets — deduplicates script tags.
 */
// Same CDN + pinned-version pattern as Leaflet itself. 1.5.3 matches the
// build the toolkit ships on customer sites, so cluster/spiderfy behaviour
// can be diffed side by side (#854).
export const LEAFLET_MARKERCLUSTER_JS = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js'

/**
 * Lazily loads Leaflet.markercluster (which extends the global L, so it must
 * come after Leaflet). Returns true once L.markerClusterGroup exists. `wanted`
 * false skips the request entirely — the plugin only ships to maps that
 * actually cluster. Deduplicates script tags like useLeaflet.
 */
export function useLeafletMarkercluster(wanted: boolean, leafletLoaded: boolean): boolean {
  const [loaded, setLoaded] = useState(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof window !== 'undefined' && typeof (window as any).L?.markerClusterGroup === 'function'
  )

  useEffect(() => {
    if (!wanted || !leafletLoaded || loaded) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (window as any).L?.markerClusterGroup === 'function') {
      setLoaded(true)
      return
    }

    const onReady = () => setLoaded(true)
    const existing = document.querySelector(`script[src="${LEAFLET_MARKERCLUSTER_JS}"]`)
    if (existing) {
      existing.addEventListener('load', onReady)
      return () => existing.removeEventListener('load', onReady)
    }

    const script = document.createElement('script')
    script.src = LEAFLET_MARKERCLUSTER_JS
    script.onload = onReady
    document.head.appendChild(script)
  }, [wanted, leafletLoaded, loaded])

  return loaded
}

export function useLeaflet(): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [loaded, setLoaded] = useState(typeof window !== 'undefined' && typeof (window as any).L !== 'undefined')

  useEffect(() => {
    if (loaded) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (window as any).L !== 'undefined') {
      setLoaded(true)
      return
    }

    // Avoid duplicate script tags
    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`)
    if (existing) {
      existing.addEventListener('load', () => setLoaded(true))
      return
    }

    const script = document.createElement('script')
    script.src = LEAFLET_JS
    script.onload = () => setLoaded(true)
    document.head.appendChild(script)
  }, [loaded])

  return loaded
}
